import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import ProfileSettings from './pages/ProfileSettings';
import Earnings from './pages/Earnings';
import CourseList from './pages/CourseList';
import CreateCourse from './pages/CreateCourse';
import CourseDetail from './pages/CourseDetail';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupSuccess from './pages/SignupSuccess';
import ForgetPassword from './pages/ForgetPassword';
import VideoPlayer from './pages/VideoPlayer';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import versionManager from './utils/versionManager'; // Import version manager

/**
 * Important: initialize isAuthenticated from localStorage synchronously.
 * This prevents a brief render where isAuthenticated === false (which caused redirect to /login)
 * before the stored auth value was loaded.
 */
function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // initialize from localStorage synchronously to avoid flashing /login on refresh
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      return localStorage.getItem('isAuthenticated') === 'true';
    } catch (err) {
      return false;
    }
  });

  // Keep localStorage in sync if isAuthenticated changes elsewhere
  useEffect(() => {
    try {
      localStorage.setItem('isAuthenticated', isAuthenticated ? 'true' : 'false');
    } catch (err) {
      // ignore localStorage errors
    }
  }, [isAuthenticated]);

  // ✅ Start version checking
  useEffect(() => {
    // Start version checking (checks every 60 seconds)
    versionManager.start(60000, (newVersion) => {
      console.log('New version available:', newVersion);
    });
    
    // Cleanup on unmount
    return () => {
      versionManager.stop();
    };
  }, []);

  return (
    <Router>
      <AppRoutes
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
      />
    </Router>
  );
}

function AppRoutes({ sidebarOpen, setSidebarOpen, isAuthenticated, setIsAuthenticated }) {
  const navigate = useNavigate();

  // Handle login -> set auth + persist + navigate to dashboard
  const handleLogin = () => {
    setIsAuthenticated(true);
    try {
      localStorage.setItem('isAuthenticated', 'true');
    } catch (err) {}
    navigate('/dashboard', { replace: true });
  };

  // Handle signup -> navigate to signup success page first
  const handleSignup = () => {
    navigate('/signup-success', { replace: true });
  };

  // Handle signup success -> set authentication and go to dashboard
  const handleSignupSuccess = () => {
    setIsAuthenticated(true);
    try {
      localStorage.setItem('isAuthenticated', 'true');
    } catch (err) {}
    navigate('/dashboard', { replace: true });
  };

  // Handle logout -> clear auth + navigate to login
  const handleLogout = () => {
    setIsAuthenticated(false);
    try {
      localStorage.removeItem('isAuthenticated');
    } catch (err) {}
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {isAuthenticated ? (
        <>
          <Sidebar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            onLogout={handleLogout}
          />

          <div className="flex-1 flex flex-col min-w-0 md:ml-72">
            <Navbar setSidebarOpen={setSidebarOpen} onLogout={handleLogout} />

            <main className="flex-1 overflow-y-auto">
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <ProfileSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/earnings"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <Earnings />
                    </ProtectedRoute>
                  }
                />

                {/* Courses nested routes */}
                <Route path="/courses">
                  <Route
                    index
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated}>
                        <CourseList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="create"
                    element={
                      <ProtectedRoute isAuthenticated={isAuthenticated}>
                        <CreateCourse />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Course Detail with nested Video Player routes */}
                  <Route path="coursedetail">
                    <Route
                      index
                      element={
                        <ProtectedRoute isAuthenticated={isAuthenticated}>
                          <CourseDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":courseId"
                      element={
                        <ProtectedRoute isAuthenticated={isAuthenticated}>
                          <CourseDetail />
                        </ProtectedRoute>
                      }
                    >
                      {/* Nested Video Player route under course detail */}
                      <Route
                        path="player/:videoId"
                        element={
                          <ProtectedRoute isAuthenticated={isAuthenticated}>
                            <VideoPlayer />
                          </ProtectedRoute>
                        }
                      />
                    </Route>
                  </Route>
                </Route>

                {/* Keep the standalone video player route for backward compatibility */}
                <Route
                  path="/player/:courseId/:videoId"
                  element={
                    <ProtectedRoute isAuthenticated={isAuthenticated}>
                      <VideoPlayer />
                    </ProtectedRoute>
                  }
                />

                {/* default route for authenticated users */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </main>
          </div>
        </>
      ) : (
        // Not authenticated: show public routes (login/signup/forget-password/signup-success)
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <Login onLogin={handleLogin} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <Signup onSignup={handleSignup} />
              </PublicRoute>
            }
          />
          <Route
            path="/signup-success"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <SignupSuccess onSignupSuccess={handleSignupSuccess} />
              </PublicRoute>
            }
          />
          <Route
            path="/forget-password"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <ForgetPassword />
              </PublicRoute>
            }
          />
           {/* New routes for Terms of Use and Privacy Policy */}
          <Route
            path="/terms"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <TermsOfUse />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy"
            element={
              <PublicRoute isAuthenticated={isAuthenticated}>
                <PrivacyPolicy />
              </PublicRoute>
            }
          />
          {/* any unknown route -> login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </div>
  );
}

// ProtectedRoute receives isAuthenticated prop
function ProtectedRoute({ children, isAuthenticated }) {
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// PublicRoute receives isAuthenticated prop
function PublicRoute({ children, isAuthenticated }) {
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
}

export default App;